import { createContext, useContext, useState, useCallback } from "react";

import type { ColumnMappingState } from "./CreateBulk/ColumnMappingScreen";

type ACHMappingState = ColumnMappingState<
  API.ACHImport.ColumnMapping,
  API.ACHImport.CSVFieldMapping
>;

type ACHImportContextType = {
  mappingState: ACHMappingState | null;
  setMappingState: (state: ACHMappingState) => void;
  nachaResponse: API.ACHImport.PaymentsResponse | null;
  csvResponse: API.ACHImport.CSVUploadResponse | null;
  importType: "nacha" | "csv" | null;
  organizationFileUuid: string | null;
  setNachaResponse: (data: API.ACHImport.PaymentsResponse) => void;
  setParsedCsvPaymentsResponse: (data: API.ACHImport.CSVParseResponse) => void;
  setCsvResponse: (data: API.ACHImport.CSVUploadResponse) => void;
  updateNachaRows: (
    updater: (rows: API.ACHImport.PaymentRow[]) => API.ACHImport.PaymentRow[]
  ) => void;
  clearImport: () => void;
};

const ACHImportContext = createContext<ACHImportContextType>({
  mappingState: null,
  setMappingState: () => {},
  nachaResponse: null,
  csvResponse: null,
  importType: null,
  organizationFileUuid: null,
  setNachaResponse: () => {},
  setParsedCsvPaymentsResponse: () => {},
  setCsvResponse: () => {},
  updateNachaRows: () => {},
  clearImport: () => {},
});

export const ACHImportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [mappingState, setMappingState] = useState<ACHMappingState | null>(
    null
  );
  const [nachaResponse, setNachaResponseState] =
    useState<API.ACHImport.PaymentsResponse | null>(null);
  const [csvResponse, setCsvResponseState] =
    useState<API.ACHImport.CSVUploadResponse | null>(null);
  const [importType, setImportType] = useState<"nacha" | "csv" | null>(null);
  const [organizationFileUuid, setOrganizationFileUuid] = useState<
    string | null
  >(null);

  const setNachaResponse = useCallback(
    (data: API.ACHImport.PaymentsResponse) => {
      setNachaResponseState(data);
      setImportType("nacha");
      setOrganizationFileUuid(data.organization_file_uuid);
    },
    []
  );

  const setParsedCsvPaymentsResponse = useCallback(
    (data: API.ACHImport.CSVParseResponse) => {
      setNachaResponseState(data);
      setImportType("csv");
      setOrganizationFileUuid(data.organization_file_uuid);
    },
    []
  );

  const setCsvResponse = useCallback(
    (data: API.ACHImport.CSVUploadResponse) => {
      setMappingState(null);
      setCsvResponseState(data);
      setImportType("csv");
    },
    []
  );

  const clearImport = useCallback(() => {
    setMappingState(null);
    setNachaResponseState(null);
    setCsvResponseState(null);
    setImportType(null);
    setOrganizationFileUuid(null);
  }, []);

  const updateNachaRows = useCallback(
    (
      updater: (rows: API.ACHImport.PaymentRow[]) => API.ACHImport.PaymentRow[]
    ) => {
      setNachaResponseState((prev) => {
        if (!prev) return prev;
        return { ...prev, rows: updater(prev.rows) };
      });
    },
    []
  );

  return (
    <ACHImportContext.Provider
      value={{
        mappingState,
        setMappingState,
        nachaResponse,
        csvResponse,
        importType,
        organizationFileUuid,
        setNachaResponse,
        setParsedCsvPaymentsResponse,
        setCsvResponse,
        updateNachaRows,
        clearImport,
      }}
    >
      {children}
    </ACHImportContext.Provider>
  );
};

export const useACHImportContext = () => useContext(ACHImportContext);

export default ACHImportContext;
